import { useEffect } from 'react'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { loadLabeling, migrateLegacySession, saveLabeling } from './labelingStorage'

/**
 * Persists labeling state to localStorage and restores it on return, keyed by
 * run id. Keying by run (not session) means labels are shared across sessions of
 * the same run and by the session-less Vet-from-file flow, and survive a session
 * switch. Selection is intentionally not persisted.
 *
 * @param runId the run these labels belong to (undefined while it is unknown)
 * @param legacySessionId the current session id, used once to migrate any labels
 *   saved under the old session-keyed storage
 */
export const useLabelingPersistence = (
  runId: string | undefined,
  legacySessionId?: number
): void => {
  const {
    labeling: { scopeKey: loadedScope, labels, labeledAt, sessionLabels, activeLabel, dirty },
    hydrate,
    resetForScope,
  } = useLabelingSlice()

  // On entering a run, restore its saved labels (migrating any legacy
  // session-keyed labels), or start fresh.
  useEffect(() => {
    if (!runId) return
    if (loadedScope === runId) return
    const saved =
      loadLabeling(runId) ??
      (legacySessionId !== undefined ? migrateLegacySession(legacySessionId, runId) : null)
    if (saved) {
      hydrate({ ...saved, scopeKey: runId })
    } else {
      resetForScope(runId)
    }
  }, [runId, legacySessionId, loadedScope, hydrate, resetForScope])

  // Persist on change, but only once the in-memory state belongs to this run, so
  // a switch never writes one run's labels under another's key.
  useEffect(() => {
    if (!runId || loadedScope !== runId) return
    saveLabeling(runId, { labels, labeledAt, sessionLabels, activeLabel, dirty })
  }, [runId, loadedScope, labels, labeledAt, sessionLabels, activeLabel, dirty])
}

export default useLabelingPersistence
